import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, CircleSlash, Info, RefreshCw, X, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { computedAttributesApi } from '@/api/endpoints/computedAttributes';
import type { ComputedAttribute } from '@/api/endpoints/computedAttributes';
import { notificationsApi } from '@/api/endpoints/notifications';
import { reportsApi } from '@/api/endpoints/reports';
import { useFleetStore } from '@/stores/fleet';
import { useEntitlements } from '@/stores/entitlements';
import { translateAttribute, formatAttributeValue, translateEvent, translateAlarm } from '@/lib/traccarLabels';
import { cn, formatDateTime } from '@/lib/utils';
import type { Event, Notification, Position } from '@/api/types';
import { parseExpression } from './customRuleExpression';

/**
 * Diagnóstico da cadeia de notificação de uma embarcação.
 *
 * ## O que resolve
 *
 * Quando um alerta "não chega", o custo está em descobrir ONDE a corrente arrebentou. São cinco
 * elos e o sintoma é idêntico em todos: silêncio. Este painel mostra os quatro que a API expõe,
 * lado a lado, para o mesmo veículo e no mesmo instante.
 *
 * | elo | de onde vem |
 * |---|---|
 * | 1. a posição chegou com o atributo | `GET /reports/route` |
 * | 2. a condição avaliou verdadeiro | `POST /attributes/computed/test` |
 * | 3. o evento nasceu | `GET /reports/events` |
 * | 4. alguma regra casa com o evento | calculado aqui, com as regras carregadas |
 * | 5. **o canal entregou** | **só o log do servidor** |
 *
 * ## O quinto elo não é observável daqui, e a tela diz isso
 *
 * É exatamente onde uma entrega morre sem deixar rastro: o notificador registra um aviso no log e
 * segue adiante, a API não devolve erro. Um painel que omitisse essa limitação daria impressão de
 * cobertura total e faria alguém concluir que está tudo certo olhando quatro de cinco elos.
 */

type Stage = 'ok' | 'fail' | 'na';

const StageIcon = ({ stage }: { stage: Stage }) => {
  if (stage === 'ok') return <CheckCircle2 className="h-4 w-4 shrink-0 text-status-running" />;
  if (stage === 'fail') return <XCircle className="h-4 w-4 shrink-0 text-status-blocked" />;
  return <CircleSlash className="h-4 w-4 shrink-0 text-muted-foreground/60" />;
};

interface ConditionResult {
  attr: ComputedAttribute;
  name: string | null;
  hit: string | null;
  err: string | null;
}

interface EventRow {
  event: Event;
  alarm: string;
  matched: Notification[];
}

export function NotificationDiagnosticsModal({
  onClose,
  initialDeviceId,
}: {
  onClose: () => void;
  initialDeviceId?: number;
}) {
  const papel = useEntitlements((s) => s.me?.user.papel ?? null);
  const isAdmin = papel === 'admin';
  const fleet = useFleetStore((s) => s.devices);
  const devices = useMemo(() => Array.from(fleet.values()).map((d) => ({ id: d.id, name: d.name })), [fleet]);

  const [deviceId, setDeviceId] = useState<number | ''>(initialDeviceId ?? '');
  const [loading, setLoading] = useState(false);
  const [auto, setAuto] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const [conditions, setConditions] = useState<ConditionResult[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [ruleCount, setRuleCount] = useState(0);
  const [lastRun, setLastRun] = useState('');
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const run = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    setError(null);
    try {
      const to = new Date();
      const from = new Date(to.getTime() - 2 * 3600 * 1000);
      const janela = { deviceId: Number(deviceId), from: from.toISOString(), to: to.toISOString() };

      const [positions, evts, notifs] = await Promise.all([
        reportsApi.route(janela).catch(() => [] as Position[]),
        reportsApi.events(janela).catch(() => [] as Event[]),
        notificationsApi.list().catch(() => [] as Notification[]),
      ]);

      setPosition(positions.length ? positions[positions.length - 1] : null);
      setRuleCount(notifs.length);

      // Elo 4: mesma lógica do servidor — tipo igual E (sem filtro de alarme OU alarme na lista).
      setEvents(
        [...evts]
          .sort((a, b) => String(b.serverTime).localeCompare(String(a.serverTime)))
          .slice(0, 40)
          .map((e) => {
            const alarm = String(e.attributes?.alarm ?? '').trim();
            const matched = notifs.filter((n) => {
              if (n.type !== e.type) return false;
              const filtro = String(n.attributes?.alarms ?? '').trim();
              if (!filtro) return true; // sem filtro = casa com qualquer alarme daquele tipo
              return filtro.split(',').map((s) => s.trim()).includes(alarm);
            });
            return { event: e, alarm, matched };
          }),
      );

      // Elo 2: só admin — o servidor exige administrador até para testar a expressão.
      if (isAdmin) {
        const attrs = await computedAttributesApi.list(Number(deviceId)).catch(() => [] as ComputedAttribute[]);
        const avaliados = await Promise.all(
          attrs
            .filter((a) => a.attribute === 'alarm')
            .map(async (attr): Promise<ConditionResult> => {
              const name = parseExpression(attr.expression)?.name?.trim() ?? null;
              try {
                const hit = await computedAttributesApi.test(Number(deviceId), {
                  description: attr.description,
                  attribute: attr.attribute,
                  type: attr.type,
                  expression: attr.expression,
                });
                return { attr, name, hit, err: null };
              } catch (e) {
                return { attr, name, hit: null, err: e instanceof Error ? e.message : 'erro ao avaliar' };
              }
            }),
        );
        setConditions(avaliados);
      } else {
        setConditions([]);
      }

      setLastRun(new Date().toLocaleTimeString('pt-BR'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha no diagnóstico.');
    } finally {
      setLoading(false);
    }
  }, [deviceId, isAdmin]);

  useEffect(() => {
    if (deviceId) void run();
  }, [deviceId, run]);

  useEffect(() => {
    if (timer.current) {
      window.clearInterval(timer.current);
      timer.current = null;
    }
    // 10s: o rastreador reporta a cada 30s ou mais, então polling mais rápido só gera requisição
    // à toa contra um dado que não mudou.
    if (auto && deviceId) timer.current = window.setInterval(() => void run(), 10000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [auto, deviceId, run]);

  const attrRows = useMemo(() => {
    const attrs = (position?.attributes ?? {}) as Record<string, unknown>;
    return Object.entries(attrs)
      .map(([k, v]) => ({ key: k, label: translateAttribute(k), value: String(formatAttributeValue(k, v)) }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, [position]);

  const alarmNaPosicao = String((position?.attributes as Record<string, unknown> | undefined)?.alarm ?? '').trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-2 sm:p-6" onClick={onClose}>
      <div
        className="flex max-h-[95vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-card shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-bold">Diagnóstico de notificação</h2>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-accent" aria-label="Fechar">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
          <select
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value ? Number(e.target.value) : '')}
            className="h-9 min-w-[14rem] flex-1 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="">— escolher embarcação —</option>
            {devices.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <Button type="button" variant="outline" size="sm" disabled={!deviceId || loading} onClick={() => void run()} className="gap-1.5">
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
            Atualizar
          </Button>
          <label className="flex cursor-pointer items-center gap-1.5 text-xs">
            <input type="checkbox" checked={auto} disabled={!deviceId} onChange={(e) => setAuto(e.target.checked)} className="h-3.5 w-3.5 accent-primary" />
            Acompanhar (10s)
          </label>
          {lastRun && <span className="text-[11px] text-muted-foreground">última leitura {lastRun}</span>}
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {!deviceId ? (
            <p className="p-6 text-center text-sm text-muted-foreground">Escolha uma embarcação para começar.</p>
          ) : (
            <>
              {error && <p className="rounded-md bg-status-blocked/10 px-3 py-2 text-xs font-medium text-status-blocked">{error}</p>}

              {/* elo 1 */}
              <section className="space-y-1.5">
                <p className="flex items-center gap-1.5 text-sm font-bold">
                  <StageIcon stage={position ? 'ok' : 'fail'} />
                  1. A posição chegou
                </p>
                {position ? (
                  <>
                    <p className="text-xs text-muted-foreground">
                      Última posição em <b className="text-foreground">{formatDateTime(position.fixTime)}</b>, {attrRows.length} atributos.{' '}
                      {alarmNaPosicao ? (
                        <>
                          Campo <code className="rounded bg-secondary px-1">alarm</code> = <b className="text-foreground">{translateAlarm(alarmNaPosicao)}</b>.
                        </>
                      ) : (
                        <>
                          Campo <code className="rounded bg-secondary px-1">alarm</code> ausente nesta posição, o que é o normal quando não há alarme agora.
                        </>
                      )}
                    </p>
                    <div className="max-h-28 overflow-y-auto rounded-md border border-border p-2">
                      <div className="flex flex-wrap gap-1">
                        {attrRows.map((a) => (
                          <span key={a.key} className="rounded-full bg-secondary px-2 py-0.5 text-[10px]">
                            {a.label} <b>{a.value}</b>
                          </span>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-xs font-medium text-status-blocked">
                    Nenhuma posição nas últimas 2 horas. A cadeia para aqui, e o problema é de comunicação, não de regra.
                  </p>
                )}
              </section>

              {/* elo 2 */}
              <section className="space-y-1.5">
                <p className="flex items-center gap-1.5 text-sm font-bold">
                  <StageIcon stage={conditions.some((c) => c.hit) ? 'ok' : conditions.some((c) => c.err) ? 'fail' : 'na'} />
                  2. Alarmes personalizados desta embarcação
                </p>
                {!isAdmin ? (
                  <p className="text-xs text-muted-foreground">
                    Só administrador pode avaliar condições: o servidor exige esse perfil até para testar.
                  </p>
                ) : conditions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nenhum alarme personalizado vinculado a esta embarcação.</p>
                ) : (
                  <ul className="space-y-1">
                    {conditions.map((c) => (
                      <li key={c.attr.id} className="flex items-start gap-1.5 text-xs">
                        <StageIcon stage={c.err ? 'fail' : c.hit ? 'ok' : 'na'} />
                        <span>
                          <b>{c.name || c.attr.description || `#${c.attr.id}`}</b>
                          {c.err ? (
                            <span className="text-status-blocked"> — erro ao avaliar: {c.err}</span>
                          ) : c.hit ? (
                            <> — satisfeita agora, gravaria <b>{c.hit}</b></>
                          ) : (
                            <span className="text-muted-foreground"> — não satisfeita na última posição</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              {/* elos 3 e 4 */}
              <section className="space-y-1.5">
                <p className="flex items-center gap-1.5 text-sm font-bold">
                  <StageIcon stage={events.length ? 'ok' : 'na'} />
                  3 e 4. Eventos das últimas 2h e regras que casam
                </p>
                {events.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nenhum evento nas últimas 2 horas.</p>
                ) : (
                  <div className="max-h-56 overflow-y-auto rounded-md border border-border">
                    <table className="w-full text-left text-xs">
                      <thead className="sticky top-0 bg-secondary/80">
                        <tr>
                          <th className="px-2 py-1.5 font-semibold">Hora</th>
                          <th className="px-2 py-1.5 font-semibold">Evento</th>
                          <th className="px-2 py-1.5 font-semibold">Alarme</th>
                          <th className="px-2 py-1.5 font-semibold">Regras que casam</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.map((row) => (
                          <tr key={row.event.id} className="border-t border-border align-top">
                            <td className="whitespace-nowrap px-2 py-1.5 text-[11px]">{formatDateTime(row.event.serverTime)}</td>
                            <td className="px-2 py-1.5 text-[11px]">{translateEvent(row.event.type)}</td>
                            <td className="px-2 py-1.5">
                              {row.alarm ? (
                                <span className="rounded-full bg-status-blocked/10 px-2 py-0.5 text-[10px] font-semibold text-status-blocked">
                                  {translateAlarm(row.alarm)}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                            <td className="px-2 py-1.5">
                              {row.matched.length ? (
                                <span className="flex flex-wrap gap-1">
                                  {row.matched.map((n) => (
                                    <span
                                      key={n.id}
                                      title={`canais: ${n.notificators || '—'}`}
                                      className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary"
                                    >
                                      {String(n.attributes?.name ?? '').trim() || `#${n.id}`}
                                    </span>
                                  ))}
                                </span>
                              ) : (
                                <span className="text-[11px] font-medium text-status-blocked">nenhuma regra sua casa com este evento</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground">
                  As regras conferidas são as {ruleCount} visíveis para você. Regra de outro usuário não aparece aqui
                  e ainda assim pode disparar.
                </p>
              </section>

              {/* elo 5 */}
              <section className="flex items-start gap-2 rounded-md bg-secondary/60 px-3 py-2">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <p className="text-[11px] text-muted-foreground">
                  <b className="text-foreground">O quinto elo não é visível por aqui.</b> Se o evento nasceu e alguma
                  regra casou, o servidor tentou despachar. Quando um canal falha nesse ponto, ele grava um aviso no
                  log e segue: a API não devolve erro e nada disso aparece nesta tela. Antes de suspeitar da regra,
                  confira no cadastro do destinatário se ele tem aparelho registrado para push e Telegram vinculado.
                </p>
              </section>
            </>
          )}
        </div>

        <footer className="flex justify-end border-t border-border bg-card px-4 py-3">
          <Button type="button" variant="outline" onClick={onClose}>Fechar</Button>
        </footer>
      </div>
    </div>
  );
}
